/**
 * StaffShiftBriefView — read-only mobile/web view that consumes
 * `GET /api/careplans/by-shift/{shiftId}/brief`. Designed to be the
 * field worker's pre-shift glance — single column, large tap targets,
 * compact metadata. Behaviour rows redact when the caller lacks
 * `behaviour.view` (server-side); internal notes redact when the
 * caller lacks `careplans.row.notes.view_internal`.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle } from 'lucide-react';

const SECTION_ICON = {
  critical_alerts: '⚠',
  communication: '💬',
  mobility: '🦽',
  personal_care: '🛁',
  nutrition: '🍽',
  toileting: '🚻',
  skin: '🩹',
  medication: '💊',
  pain_sleep: '🌙',
  behaviour: '🧠',
  goals: '🎯',
  emergency_eol: '🚨',
};

export function StaffShiftBriefView({ shiftId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/careplans/by-shift/${shiftId}/brief`)
      .then((r) => setData(r.data.data))
      .catch((e) => setError(e?.response?.data?.error?.message || 'Brief unavailable'))
      .finally(() => setLoading(false));
  }, [shiftId]);

  if (loading) {
    return (
      <Card data-testid="staff-shift-brief-loading">
        <CardContent className="p-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading shift brief…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-rose-700" data-testid="staff-shift-brief-error">
          {error}
        </CardContent>
      </Card>
    );
  }
  if (!data?.has_plan) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground" data-testid="staff-shift-brief-empty">
          No approved care plan is linked to this shift yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="staff-shift-brief-view">
      <Card>
        <CardContent className="p-3 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold">Pre-Shift Brief</h3>
            <Badge variant="outline" className="bg-zinc-50 text-zinc-700 border-zinc-200 text-[10px]">
              plan v{data.plan_version}
            </Badge>
            {data.last_acknowledged_at && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                acknowledged {String(data.last_acknowledged_at).slice(0, 16)}
              </Badge>
            )}
          </div>
          {(data.medical_alerts_summary || data.allergies_summary) && (
            <div className="text-xs text-rose-700 flex gap-2 items-start mt-1">
              <AlertTriangle className="h-3 w-3 mt-0.5" />
              <span>
                {data.medical_alerts_summary && <><strong>Alerts:</strong> {data.medical_alerts_summary}. </>}
                {data.allergies_summary && <><strong>Allergies:</strong> {data.allergies_summary}.</>}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {(data.sections || []).map((s) => (
        <Card key={s.section_key} data-testid={`staff-shift-brief-section-${s.section_key}`}>
          <CardContent className="p-3 space-y-2">
            <div className="font-semibold text-sm flex items-center gap-1.5">
              <span aria-hidden>{SECTION_ICON[s.section_key] || '•'}</span>
              {s.title}
            </div>
            <ul className="space-y-1.5">
              {s.rows.map((r) => (
                <li key={r.id} className="border rounded p-2"
                    data-testid={`staff-shift-brief-row-${r.id}`}>
                  <div className="text-sm">
                    {r.intervention_or_staff_instruction}
                  </div>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{r.support_level || 'SUPERVISION'}</Badge>
                    <Badge variant="outline" className="text-[10px]">{r.frequency || 'as_required'}</Badge>
                    {r.risk_level && r.risk_level !== 'LOW' && (
                      <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                        risk {r.risk_level}
                      </Badge>
                    )}
                    {r.escalation_required && r.escalation_instruction && (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                        escalation
                      </Badge>
                    )}
                  </div>
                  {r.escalation_required && r.escalation_instruction && (
                    <div className="text-xs mt-1 text-amber-800">
                      Escalation: {r.escalation_instruction}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default StaffShiftBriefView;
